import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  Identifier,
  IdentifierCreate,
  IdentifierList,
  IdentifierPatch,
  IdentifierPayload,
  IdentifierType,
  IdentifierTypeFilterParams,
  IdentifierTypeList,
} from "src/util/types/dataModels/identifier"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_DELETE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_IDENTIFIERS,
} from "src/util/types/dataModels/user"

export async function getIdentifiersList(
  config: HttpConfig<IdentifierTypeFilterParams> = {}
): Promise<IdentifierList> {
  const entityURL = CSIurl.IDENTIFIERS_LIST
  const url = concatUrl(entityURL, { ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_IDENTIFIERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getIdentifiersListByType<IdenType extends IdentifierType>(
  config: HttpConfig<IdentifierTypeFilterParams> = {},
  idenType: IdenType
): Promise<IdentifierTypeList<IdenType>> {
  const entityURL = CSIurl.IDENTIFIERS_TYPE.replace("{iden_type}", idenType)
  const url = concatUrl(entityURL, { ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_IDENTIFIERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postIdentifier(
  config: HttpConfig = {},
  idenType: IdentifierType,
  identifierData: IdentifierCreate
): Promise<Identifier> {
  const url = CSIurl.IDENTIFIERS_TYPE.replace("{iden_type}", idenType)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, identifierData, { signal, customConfig })

  return data
}

export async function getIdentifier(
  config: HttpConfig = {},
  idenType: IdentifierType,
  idenID: string
): Promise<Identifier> {
  const url = CSIurl.IDENTIFIERS_TYPE_ID.replace("{iden_type}", idenType).replace(
    "{iden_id}",
    idenID
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_IDENTIFIERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putIdentifier(
  config: HttpConfig = {},
  idenType: IdentifierType,
  idenID: string,
  identifierData: IdentifierPayload
): Promise<Identifier> {
  const url = CSIurl.IDENTIFIERS_TYPE_ID.replace("{iden_type}", idenType).replace(
    "{iden_id}",
    idenID
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, identifierData, { signal, customConfig })

  return data
}

export async function patchIdentifier(
  config: HttpConfig = {},
  idenType: IdentifierType,
  idenID: string,
  identifierData: IdentifierPatch
): Promise<Identifier> {
  const url = CSIurl.IDENTIFIERS_TYPE_ID.replace("{iden_type}", idenType).replace(
    "{iden_id}",
    idenID
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, identifierData, { signal, customConfig })

  return data
}

export async function deleteIdentifier(
  config: HttpConfig = {},
  idenType: IdentifierType,
  idenID: string
) {
  const url = CSIurl.IDENTIFIERS_TYPE_ID.replace("{iden_type}", idenType).replace(
    "{iden_id}",
    idenID
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function postIdentifiersBatch(
  config: HttpConfig = {},
  identifierData: Array<IdentifierPayload>
): Promise<IdentifierList> {
  const url = CSIurl.IDENTIFIERS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, identifierData, { signal, customConfig })

  return data
}

export async function patchIdentifiersBatch(
  config: HttpConfig = {},
  identifierData: Array<IdentifierPatch>
): Promise<IdentifierList> {
  const url = CSIurl.IDENTIFIERS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, identifierData, { signal, customConfig })

  return data
}

export async function deleteIdentifiersBatch(
  config: HttpConfig = {},
  identifierData: Array<Identifier>
) {
  const url = CSIurl.IDENTIFIERS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_IDENTIFIERS,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { data: identifierData, signal, customConfig })
}
